import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  useCustomer,
  useCustomerAddresses,
  useUpsertCustomer,
  useDeleteCustomer,
  useUpsertAddress,
  useDeleteAddress,
  type CustomerAddress,
  type CustomerType,
} from "@/lib/db";
import { geocode } from "@/lib/api";

export default function KundeDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: customer, isLoading } = useCustomer(id);
  const { data: addresses = [] } = useCustomerAddresses(id);
  const upsert = useUpsertCustomer();
  const del = useDeleteCustomer();

  const [tab, setTab] = useState<"stamm" | "adressen" | "sepa" | "bestellungen">("stamm");

  if (isLoading) return <div className="card">Lade…</div>;
  if (!customer) return <div className="card">Kunde nicht gefunden. <Link to="/backoffice/kunden" className="underline text-wiese-700">Zurück</Link></div>;

  async function onDelete() {
    if (!confirm(`Kunde "${customer!.display_name}" und alle Adressen wirklich löschen?`)) return;
    await del.mutateAsync(customer!.id);
    nav("/backoffice/kunden");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Link to="/backoffice/kunden" className="text-sm text-hof-600 hover:text-hof-900">← zur Liste</Link>
          <h1 className="text-3xl font-bold text-hof-900">{customer.display_name}</h1>
          <div className="text-hof-600 mt-1">
            {customer.type === "gewerblich" ? <span className="pill-info">Gewerblich</span> : <span className="pill bg-hof-100 text-hof-800">Privatkunde</span>}
            {!customer.is_active && <span className="pill-warn ml-2">inaktiv</span>}
          </div>
        </div>
        <button onClick={onDelete} className="btn-danger">Kunde löschen</button>
      </div>

      <nav className="flex gap-1 border-b border-hof-200 overflow-x-auto">
        <TabBtn active={tab === "stamm"} onClick={() => setTab("stamm")}>Stammdaten</TabBtn>
        <TabBtn active={tab === "adressen"} onClick={() => setTab("adressen")}>Adressen ({addresses.length})</TabBtn>
        <TabBtn active={tab === "sepa"} onClick={() => setTab("sepa")}>SEPA-Mandat</TabBtn>
        <TabBtn active={tab === "bestellungen"} onClick={() => setTab("bestellungen")}>Bestellungen</TabBtn>
      </nav>

      {tab === "stamm" && (
        <StammForm
          initial={customer}
          onSubmit={async (next) => {
            await upsert.mutateAsync({ id: customer.id, ...next });
          }}
        />
      )}
      {tab === "adressen" && <AdressenTab customerId={customer.id} addresses={addresses} />}
      {tab === "sepa" && <div className="card text-hof-600">SEPA-Mandat-Verwaltung kommt in Modul 5 (Zahlung).</div>}
      {tab === "bestellungen" && <div className="card text-hof-600">Bestellliste kommt in Modul 3 (Bestellung & Abo).</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-3 -mb-px border-b-2 whitespace-nowrap ${
        active ? "border-wiese-600 text-wiese-700 font-medium" : "border-transparent text-hof-600 hover:text-hof-900"
      }`}>
      {children}
    </button>
  );
}

function StammForm({
  initial,
  onSubmit,
}: {
  initial: { display_name: string; type: CustomerType; phone: string | null; email: string | null; notes: string | null; is_active: boolean };
  onSubmit: (next: { display_name: string; type: CustomerType; phone: string | null; email: string | null; notes: string | null; is_active: boolean }) => Promise<void>;
}) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      await onSubmit(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <div>
        <label className="label">Name *</label>
        <input className="input" value={form.display_name}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })} required />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">Typ</label>
          <select className="input" value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as CustomerType })}>
            <option value="privat">Privatkunde</option>
            <option value="gewerblich">Gewerblich</option>
          </select>
        </div>
        <div>
          <label className="label">Telefon</label>
          <input className="input" type="tel" value={form.phone ?? ""}
            onChange={(e) => setForm({ ...form, phone: e.target.value || null })} />
        </div>
      </div>
      <div>
        <label className="label">E-Mail</label>
        <input className="input" type="email" value={form.email ?? ""}
          onChange={(e) => setForm({ ...form, email: e.target.value || null })} />
      </div>
      <div>
        <label className="label">Interne Notizen</label>
        <textarea className="input" rows={3} value={form.notes ?? ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value || null })} />
      </div>
      <label className="flex items-center gap-3 min-h-touch cursor-pointer">
        <input type="checkbox" checked={form.is_active}
          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          className="w-5 h-5" />
        <span>Kunde aktiv (erscheint in Bestelllisten)</span>
      </label>
      {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}
      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? "Speichern…" : "Speichern"}
        </button>
        {saved && <span className="text-wiese-700 text-sm">✓ gespeichert</span>}
      </div>
    </form>
  );
}

function AdressenTab({ customerId, addresses }: { customerId: string; addresses: CustomerAddress[] }) {
  const [editing, setEditing] = useState<CustomerAddress | "new" | null>(null);
  return (
    <div className="space-y-3">
      <button onClick={() => setEditing("new")} className="btn-primary">+ Neue Adresse</button>
      {addresses.length === 0 ? (
        <div className="card text-center text-hof-600">Noch keine Adresse — leg die erste an.</div>
      ) : (
        <div className="space-y-2">
          {addresses.map((a) => (
            <div key={a.id} className="card flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-hof-900">{a.label ?? "Adresse"}</span>
                  {a.is_default && <span className="pill-success">Standard</span>}
                  {a.distance_km_from_hof != null && (
                    <span className={a.distance_km_from_hof <= 100 ? "pill bg-hof-100 text-hof-800" : "pill-danger"}>
                      {a.distance_km_from_hof.toFixed(1)} km
                    </span>
                  )}
                </div>
                <div className="text-hof-700 mt-1">
                  {a.street}, {a.zip} {a.city}
                </div>
                {a.delivery_notes && <div className="text-sm text-hof-500 mt-1">Hinweis: {a.delivery_notes}</div>}
              </div>
              <button onClick={() => setEditing(a)} className="btn-ghost p-2" aria-label="Bearbeiten">✎</button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <AdressDrawer
          customerId={customerId}
          initial={editing === "new" ? null : editing}
          isFirstAddress={addresses.length === 0}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function AdressDrawer({
  customerId,
  initial,
  isFirstAddress,
  onClose,
}: {
  customerId: string;
  initial: CustomerAddress | null;
  isFirstAddress: boolean;
  onClose: () => void;
}) {
  const upsert = useUpsertAddress();
  const del = useDeleteAddress();
  const [form, setForm] = useState({
    id: initial?.id,
    label: initial?.label ?? "",
    street: initial?.street ?? "",
    zip: initial?.zip ?? "",
    city: initial?.city ?? "",
    delivery_notes: initial?.delivery_notes ?? "",
    is_default: initial?.is_default ?? isFirstAddress,
    geo_lat: initial?.geo_lat ?? null,
    geo_lng: initial?.geo_lng ?? null,
    distance_km_from_hof: initial?.distance_km_from_hof ?? null,
  });
  const [geocoding, setGeocoding] = useState(false);
  const [geoMsg, setGeoMsg] = useState<{ kind: "ok" | "warn" | "err"; text: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onGeocode() {
    setGeoMsg(null);
    if (!form.street || !form.zip || !form.city) {
      setGeoMsg({ kind: "err", text: "Strasse, PLZ und Ort ausfüllen, dann nochmal." });
      return;
    }
    setGeocoding(true);
    try {
      const r = await geocode(`${form.street}, ${form.zip} ${form.city}`);
      setForm((f) => ({
        ...f,
        geo_lat: r.lat,
        geo_lng: r.lng,
        distance_km_from_hof: r.distance_km,
      }));
      if (r.in_delivery_zone) {
        setGeoMsg({ kind: "ok", text: `${r.display_name} (${r.distance_km} km vom Hof)` });
      } else {
        setGeoMsg({ kind: "warn", text: `Adresse ist ${r.distance_km} km entfernt — außerhalb des 100 km-Liefergebiets.` });
      }
    } catch (ex) {
      setGeoMsg({ kind: "err", text: (ex as Error).message });
    } finally {
      setGeocoding(false);
    }
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      await upsert.mutateAsync({
        ...form,
        customer_id: customerId,
        label: form.label || null,
        delivery_notes: form.delivery_notes || null,
      });
      onClose();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!form.id) return;
    if (!confirm("Diese Adresse wirklich löschen?")) return;
    await del.mutateAsync({ id: form.id, customerId });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-hof-900/40 flex justify-end" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSave}
        className="bg-hof-50 w-full max-w-md h-full overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-hof-900">{initial ? "Adresse bearbeiten" : "Neue Adresse"}</h2>
          <button type="button" onClick={onClose} className="btn-ghost p-2">✕</button>
        </div>

        <div>
          <label className="label">Label (optional)</label>
          <input className="input" value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="z.B. Privat, Filiale Bielefeld" />
        </div>
        <div>
          <label className="label">Strasse + Hausnr *</label>
          <input className="input" value={form.street}
            onChange={(e) => setForm({ ...form, street: e.target.value })} required />
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <div>
            <label className="label">PLZ *</label>
            <input className="input" value={form.zip} inputMode="numeric"
              onChange={(e) => setForm({ ...form, zip: e.target.value })} required />
          </div>
          <div>
            <label className="label">Ort *</label>
            <input className="input" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} required />
          </div>
        </div>

        <button type="button" onClick={onGeocode} className="btn-secondary w-full" disabled={geocoding}>
          {geocoding ? "Suche Koordinaten…" : "📍 Adresse prüfen & geocodieren"}
        </button>
        {geoMsg && (
          <div className={`rounded-xl px-4 py-3 text-sm ${
            geoMsg.kind === "ok"   ? "bg-wiese-50 border border-wiese-200 text-wiese-800" :
            geoMsg.kind === "warn" ? "bg-sonne-50 border border-sonne-200 text-sonne-800" :
                                     "bg-red-50 border border-red-200 text-red-800"
          }`}>
            {geoMsg.text}
          </div>
        )}

        <div>
          <label className="label">Lieferhinweis</label>
          <textarea className="input" rows={2} value={form.delivery_notes}
            onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })}
            placeholder="z.B. Hund im Hof, hinten klingeln" />
        </div>

        <label className="flex items-center gap-3 min-h-touch cursor-pointer">
          <input type="checkbox" checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            className="w-5 h-5" />
          <span>Standard-Lieferadresse</span>
        </label>

        {err && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm">{err}</div>}

        <div className="flex gap-2 pt-2">
          <button type="submit" className="btn-primary flex-1" disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </button>
          {form.id && (
            <button type="button" onClick={onDelete} className="btn-danger">Löschen</button>
          )}
        </div>
      </form>
    </div>
  );
}
